import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';
import { executeSyncForCompany } from '../../../lib/syncEngine';

/**
 * S2.7 + S3: Sync endpoint - triggers data synchronization from Cegid.
 * POST /api/cegid/sync { companyCode, syncTypes, ejercicio }
 * GET  /api/cegid/sync - returns recent sync runs
 * Only admin can trigger syncs.
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  const supabaseAdmin = getSupabaseAdminClient();

  // GET: Return recent sync runs
  if (req.method === 'GET') {
    try {
      const { data: runs, error } = await supabaseAdmin
        .from('cegid_sync_runs')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      return res.status(200).json({ success: true, data: runs });
    } catch (error: any) {
      return res.status(500).json({ error: 'Error al obtener historial de sincronización.' });
    }
  }

  // POST: Trigger a sync
  if (req.method === 'POST') {
    const { companyCode, syncTypes, ejercicio } = req.body || {};

    if (!companyCode) {
      return res.status(400).json({ error: 'Se requiere companyCode.' });
    }

    const currentYear = ejercicio || new Date().getFullYear().toString();

    // Look up company mapping to determine tipoOpcionFiscal
    const { data: mapping } = await supabaseAdmin
      .from('cegid_company_mapping')
      .select('tipo_opcion_fiscal')
      .eq('cegid_company_code', companyCode)
      .single();

    const tipoFiscal = mapping?.tipo_opcion_fiscal ?? 1;

    // Auto-select sync types based on tipoOpcionFiscal
    const defaultTypes = tipoFiscal === 1
      ? ['facturas', 'apuntes', 'subcuentas']
      : ['facturasEstimaciones', 'clientes', 'proveedores'];
    const typesToSync = syncTypes || defaultTypes;

    // Create sync run record
    const { data: syncRun, error: createError } = await supabaseAdmin
      .from('cegid_sync_runs')
      .insert({
        sync_type: typesToSync.join(','),
        status: 'running',
        created_by: user.id,
        metadata: { companyCode, ejercicio: currentYear, syncTypes: typesToSync },
      })
      .select('id')
      .single();

    if (createError || !syncRun) {
      return res.status(500).json({ error: 'Error al crear registro de sincronización.' });
    }

    try {
      const result = await executeSyncForCompany(supabaseAdmin, companyCode, syncRun.id, {
        syncTypes: typesToSync,
        ejercicio: currentYear,
        tipoOpcionFiscal: tipoFiscal,
      });

      return res.status(200).json({
        success: true,
        ...result,
      });
    } catch (error: any) {
      return res.status(500).json({ error: 'Error durante la sincronización.', details: error.message });
    }
  }

  return res.status(405).json({ error: 'Method not allowed' });
}

export default withAuth(handler, ['admin']);
